/**
 * PostEditor - cria ou edita publicações.
 *
 * Fluxo principal:
 *   1) Selecionar tipo de publicação (Feed 1080x1350 / Stories 1080x1920 / Quadrado 1080x1080)
 *   2) Carregar imagem ou vídeo (AÇÃO PRIMÁRIA via drag&drop ou click)
 *   3) (Alternativa) Gerar imagem com IA — botão secundário
 *   4) Escrever legenda (manual ou IA)
 *   5) Selecionar canais e agendar
 */
import { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
    Sparkles,
    Hash,
    ImagePlus,
    Save,
    CalendarClock,
    Loader2,
    Lightbulb,
    Wand2,
    Send,
    Trash2,
    Upload,
    Image as ImageIcon,
    Film,
    LayoutGrid,
    Smartphone,
    Square,
    Check,
} from "lucide-react";
import { CHANNELS } from "@/lib/channels";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
    POST_FORMATS,
    DEFAULT_POST_FORMAT,
    getPostFormat,
    validateMediaFile,
    readFileAsDataUrl,
} from "@/lib/postFormats";

const TONES = [
    { v: "profissional", label: "Profissional" },
    { v: "amigavel", label: "Amigável" },
    { v: "promocional", label: "Promocional" },
    { v: "inspirador", label: "Inspirador" },
    { v: "divertido", label: "Divertido" },
];

const FORMAT_ICONS = {
    feed: LayoutGrid,
    stories: Smartphone,
    quadrado: Square,
};

export default function PostEditor({ open, onOpenChange, post, onSaved }) {
    // Texto
    const [topic, setTopic] = useState("");
    const [tone, setTone] = useState("amigavel");
    const [niche, setNiche] = useState("");
    const [caption, setCaption] = useState("");
    const [hashtags, setHashtags] = useState([]);

    // Mídia
    const [postFormat, setPostFormat] = useState(DEFAULT_POST_FORMAT);
    const [mediaUrl, setMediaUrl] = useState(""); // data URL ou URL hosted
    const [mediaType, setMediaType] = useState("image"); // image | video
    const [mediaName, setMediaName] = useState("");
    const [dragOver, setDragOver] = useState(false);

    // Distribuição
    const [channels, setChannels] = useState(["instagram"]);
    const [scheduledFor, setScheduledFor] = useState("");

    // IA loaders
    const [loadingCaption, setLoadingCaption] = useState(false);
    const [loadingHashtags, setLoadingHashtags] = useState(false);
    const [loadingImage, setLoadingImage] = useState(false);
    const [loadingIdeas, setLoadingIdeas] = useState(false);
    const [ideas, setIdeas] = useState([]);
    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            if (post) {
                setCaption(post.caption || "");
                setHashtags(post.hashtags || []);
                const m = post.media?.[0];
                setMediaUrl(m?.url || "");
                setMediaType(m?.type || "image");
                setMediaName("");
                setChannels(post.channels || ["instagram"]);
                setPostFormat(post.post_format || DEFAULT_POST_FORMAT);
                setScheduledFor(
                    post.scheduled_for
                        ? new Date(post.scheduled_for).toISOString().slice(0, 16)
                        : ""
                );
                setTopic("");
            } else {
                setCaption("");
                setHashtags([]);
                setMediaUrl("");
                setMediaType("image");
                setMediaName("");
                setChannels(["instagram"]);
                setPostFormat(DEFAULT_POST_FORMAT);
                setScheduledFor("");
                setTopic("");
                setIdeas([]);
            }
        }
    }, [open, post]);

    const formatInfo = getPostFormat(postFormat);

    const toggleChannel = (t) => {
        setChannels((c) =>
            c.includes(t) ? c.filter((x) => x !== t) : [...c, t]
        );
    };

    // ====== UPLOAD ======
    const handleFile = async (file) => {
        if (!file) return;
        const v = validateMediaFile(file);
        if (!v.ok) {
            toast.error(v.message);
            return;
        }
        try {
            const dataUrl = await readFileAsDataUrl(file);
            setMediaUrl(dataUrl);
            setMediaType(v.kind);
            setMediaName(file.name);
            toast.success(`${v.kind === "image" ? "Imagem" : "Vídeo"} carregado.`);
        } catch (err) {
            toast.error("Erro ao ler ficheiro.");
        }
    };

    const onFilePick = (e) => {
        handleFile(e.target.files?.[0]);
        e.target.value = ""; // reset
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) handleFile(f);
    };

    const removeMedia = () => {
        setMediaUrl("");
        setMediaType("image");
        setMediaName("");
    };

    // ====== AI HANDLERS ======
    const generateCaption = async () => {
        if (!topic) { toast.error("Indique o tópico ou ideia."); return; }
        setLoadingCaption(true);
        try {
            const { data } = await api.post("/ai/caption", {
                topic,
                tone,
                niche,
                channel: channels[0] || "instagram",
            });
            setCaption(data.caption);
            toast.success("Legenda gerada!");
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setLoadingCaption(false);
        }
    };

    const generateHashtags = async () => {
        if (!topic && !caption) { toast.error("Adicione um tópico ou legenda."); return; }
        setLoadingHashtags(true);
        try {
            const { data } = await api.post("/ai/hashtags", {
                topic: topic || caption.slice(0, 100),
                niche,
                count: 12,
            });
            setHashtags(data.hashtags);
            toast.success("Hashtags geradas!");
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setLoadingHashtags(false);
        }
    };

    const generateImage = async () => {
        const prompt = topic || caption.slice(0, 150) || "Social media post";
        setLoadingImage(true);
        try {
            const { data } = await api.post("/ai/image", {
                prompt,
                format: postFormat,
                aspect: formatInfo.aiAspect,
            });
            setMediaUrl(data.image_data_url);
            setMediaType("image");
            setMediaName(`gerada-ia-${formatInfo.id}.png`);
            toast.success(`Imagem ${formatInfo.dimensions} gerada!`);
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setLoadingImage(false);
        }
    };

    const generateIdeas = async () => {
        if (!niche) { toast.error("Indique o seu nicho primeiro."); return; }
        setLoadingIdeas(true);
        try {
            const { data } = await api.post("/ai/ideas", { niche, count: 5 });
            setIdeas(data.ideas);
            toast.success("5 ideias geradas!");
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setLoadingIdeas(false);
        }
    };

    // ====== SAVE ======
    const save = async (asStatus) => {
        const finalStatus = asStatus || (scheduledFor ? "agendado" : "rascunho");
        // Para publicar/agendar, exigir conteúdo. Rascunho pode estar vazio.
        if (finalStatus !== "rascunho" && !caption && !mediaUrl) {
            toast.error("Adicione legenda, imagem ou vídeo antes de publicar/agendar.");
            return;
        }
        if (channels.length === 0) {
            toast.error("Selecione pelo menos uma rede.");
            return;
        }
        setSaving(true);
        try {
            const body = {
                caption,
                hashtags,
                media: mediaUrl ? [{ type: mediaType, url: mediaUrl }] : [],
                channels,
                post_format: postFormat,
                status: finalStatus,
                scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : null,
            };
            if (post) {
                await api.put(`/posts/${post.id}`, body);
                toast.success("Publicação atualizada!");
            } else {
                await api.post("/posts", body);
                toast.success(
                    finalStatus === "agendado"
                        ? "Publicação agendada!"
                        : finalStatus === "publicado"
                            ? "Publicação publicada!"
                            : "Rascunho guardado."
                );
            }
            onSaved?.();
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-5xl p-0 max-h-[92vh] overflow-y-auto scrollbar-thin">
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle className="font-display text-xl">
                        {post ? "Editar publicação" : "Nova publicação"}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid lg:grid-cols-2 gap-0">
                    {/* LEFT: editor */}
                    <div className="p-6 space-y-5 lg:border-r border-border">
                        {/* === FORMATO DE PUBLICAÇÃO === */}
                        <div>
                            <Label className="mb-2 block">Tipo de publicação</Label>
                            <div className="grid grid-cols-3 gap-2" data-testid="post-format-selector">
                                {POST_FORMATS.map((f) => {
                                    const Icon = FORMAT_ICONS[f.id] || Square;
                                    const active = postFormat === f.id;
                                    return (
                                        <button
                                            key={f.id}
                                            type="button"
                                            onClick={() => setPostFormat(f.id)}
                                            className={`p-3 rounded-xl border text-left transition-colors ${
                                                active
                                                    ? "border-[#6B21D9] bg-[#6B21D9]/10 ring-1 ring-[#6B21D9]/30"
                                                    : "border-white/10 hover:border-white/20 hover:bg-white/5"
                                            }`}
                                            data-testid={`post-format-${f.id}`}
                                        >
                                            <div className="flex items-center justify-between mb-1.5">
                                                <Icon className={`h-4 w-4 ${active ? "text-[#A78BFA]" : "text-muted-foreground"}`} />
                                                {active && <Check className="h-3.5 w-3.5 text-[#2DD4BF]" />}
                                            </div>
                                            <div className={`font-semibold text-sm ${active ? "text-foreground" : "text-foreground/90"}`}>{f.label}</div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5">{f.dimensions}</div>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                {formatInfo.description}
                            </p>
                        </div>

                        {/* === MÍDIA (UPLOAD PRIMÁRIO) === */}
                        <div>
                            <Label className="mb-2 block">
                                Mídia <span className="text-muted-foreground text-xs font-normal">· imagem ou vídeo</span>
                            </Label>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                                className="hidden"
                                onChange={onFilePick}
                                data-testid="post-editor-media-file-input"
                            />

                            {!mediaUrl ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={onDrop}
                                        className={`mt-1 w-full ${formatInfo.aspectClass} max-h-[420px] rounded-xl border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 text-center p-6 ${
                                            dragOver
                                                ? "border-[#6B21D9] bg-[#6B21D9]/10"
                                                : "border-white/15 hover:border-[#6B21D9]/50 hover:bg-white/5"
                                        }`}
                                        data-testid="post-editor-media-dropzone"
                                    >
                                        <div className="h-12 w-12 rounded-2xl btn-grad-purple grid place-items-center text-white">
                                            <Upload className="h-5 w-5" />
                                        </div>
                                        <div className="font-semibold text-sm">Carregar imagem ou vídeo</div>
                                        <div className="text-xs text-muted-foreground">
                                            Arraste e largue o ficheiro ou clique para escolher
                                        </div>
                                        <div className="text-[11px] text-muted-foreground/80">
                                            JPG · PNG · WEBP até 10 MB · MP4 · MOV · WEBM até 25 MB
                                        </div>
                                        <div className="text-[11px] text-[#A78BFA] mt-1">
                                            Recomendado: {formatInfo.dimensions}
                                        </div>
                                    </button>

                                    {/* Alternativa secundária: gerar com IA */}
                                    <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2">
                                        <span className="text-xs text-muted-foreground flex-1 min-w-[140px]">
                                            Não tem ficheiro? Pode gerar com IA:
                                        </span>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={generateImage}
                                            disabled={loadingImage}
                                            data-testid="post-editor-generate-image-button"
                                        >
                                            {loadingImage
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : <Sparkles className="h-3.5 w-3.5" />}
                                            <span className="ml-1.5">Gerar imagem com IA</span>
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="mt-1 space-y-2">
                                    <div
                                        className={`relative rounded-xl overflow-hidden border border-white/10 bg-black ${formatInfo.aspectClass} max-h-[480px]`}
                                        data-testid="post-editor-media-preview"
                                    >
                                        {mediaType === "video" ? (
                                            <video
                                                src={mediaUrl}
                                                controls
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <img
                                                src={mediaUrl}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                        <Badge className="absolute top-2 left-2 bg-black/60 text-white border-none gap-1 text-[10px]">
                                            {mediaType === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                                            {formatInfo.dimensions}
                                        </Badge>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            data-testid="post-editor-media-replace"
                                        >
                                            <Upload className="h-3.5 w-3.5 mr-1.5" /> Trocar
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={generateImage}
                                            disabled={loadingImage}
                                            data-testid="post-editor-regenerate-image"
                                        >
                                            {loadingImage
                                                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                                : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                                            Gerar com IA
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={removeMedia}
                                            data-testid="post-editor-remove-media"
                                        >
                                            <Trash2 className="h-3.5 w-3.5 text-[#FB7185]" />
                                        </Button>
                                        {mediaName && (
                                            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                                                {mediaName}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* === IA · TEXTO === */}
                        <div className="rounded-xl border border-white/5 bg-secondary/30 p-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-[#A78BFA]" />
                                <span className="text-sm font-semibold">Assistente IA (texto)</span>
                                <span className="text-xs text-muted-foreground">opcional</span>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Nicho</Label>
                                    <Input
                                        placeholder="Pasteleira, imobiliária..."
                                        value={niche}
                                        onChange={(e) => setNiche(e.target.value)}
                                        data-testid="editor-niche-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Tom</Label>
                                    <Select value={tone} onValueChange={setTone}>
                                        <SelectTrigger data-testid="editor-tone-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {TONES.map((t) => (
                                                <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Tópico/ideia para a IA</Label>
                                <Input
                                    placeholder="Ex: lançamento de bolo de anánás"
                                    value={topic}
                                    onChange={(e) => setTopic(e.target.value)}
                                    data-testid="editor-topic-input"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    onClick={generateCaption}
                                    disabled={loadingCaption}
                                    size="sm"
                                    className="btn-grad-purple text-white"
                                    data-testid="post-editor-generate-caption-button"
                                >
                                    {loadingCaption ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                                    <span className="ml-1.5">Gerar legenda</span>
                                </Button>
                                <Button
                                    onClick={generateHashtags}
                                    disabled={loadingHashtags}
                                    size="sm"
                                    variant="outline"
                                    data-testid="post-editor-generate-hashtags-button"
                                >
                                    {loadingHashtags ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hash className="h-3.5 w-3.5" />}
                                    <span className="ml-1.5">Hashtags</span>
                                </Button>
                                <Button
                                    onClick={generateIdeas}
                                    disabled={loadingIdeas}
                                    size="sm"
                                    variant="outline"
                                    data-testid="post-editor-generate-ideas-button"
                                >
                                    {loadingIdeas ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lightbulb className="h-3.5 w-3.5" />}
                                    <span className="ml-1.5">Ideias</span>
                                </Button>
                            </div>

                            {ideas.length > 0 && (
                                <Card className="p-3 rounded-xl bg-[#6B21D9]/8 border-[#6B21D9]/20 space-y-2">
                                    <div className="text-xs font-semibold text-[#A78BFA] flex items-center gap-1">
                                        <Sparkles className="h-3 w-3" /> Ideias da IA
                                    </div>
                                    {ideas.map((id, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => {
                                                setTopic(id.title);
                                                toast.info(`Tema selecionado: ${id.title}`);
                                            }}
                                            className="text-left w-full p-2 rounded-lg hover:bg-white/5"
                                            data-testid={`editor-idea-${i}`}
                                        >
                                            <div className="text-sm font-medium">{id.title}</div>
                                            <div className="text-xs text-muted-foreground">{id.description} · <span className="text-[#A78BFA]">{id.format}</span></div>
                                        </button>
                                    ))}
                                </Card>
                            )}
                        </div>

                        {/* === LEGENDA === */}
                        <div>
                            <Label>Legenda</Label>
                            <Textarea
                                rows={6}
                                placeholder="Escreva a legenda ou clique em ‘Gerar legenda’…"
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                data-testid="post-editor-caption-textarea"
                            />
                            <div className="text-xs text-muted-foreground mt-1">{caption.length} caracteres</div>
                        </div>

                        {hashtags.length > 0 && (
                            <div>
                                <Label>Hashtags ({hashtags.length})</Label>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {hashtags.map((h, i) => (
                                        <Badge key={i} variant="outline" className="border-[#6B21D9]/30 bg-[#6B21D9]/10 text-[#A78BFA] gap-1">
                                            #{h}
                                            <button
                                                onClick={() => setHashtags(hashtags.filter((_, j) => j !== i))}
                                                className="hover:text-white"
                                            >×</button>
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* === CANAIS === */}
                        <div>
                            <Label>Canais</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {CHANNELS.map((c) => {
                                    const Icon = c.icon;
                                    const active = channels.includes(c.type);
                                    return (
                                        <button
                                            key={c.type}
                                            type="button"
                                            onClick={() => toggleChannel(c.type)}
                                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium flex items-center gap-1.5 ${
                                                active
                                                    ? "border-[#6B21D9] bg-[#6B21D9]/15 text-[#A78BFA]"
                                                    : "border-white/10 text-muted-foreground hover:bg-white/5"
                                            }`}
                                            data-testid={`post-editor-channel-${c.type}`}
                                        >
                                            <Icon className="h-3.5 w-3.5" /> {c.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* === AGENDAR === */}
                        <div>
                            <Label>Agendar para (opcional)</Label>
                            <Input
                                type="datetime-local"
                                value={scheduledFor}
                                onChange={(e) => setScheduledFor(e.target.value)}
                                data-testid="post-editor-schedule-input"
                            />
                        </div>
                    </div>

                    {/* RIGHT: previews */}
                    <div className="p-6 bg-secondary/40 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display font-semibold">Pré-visualização</h3>
                            <Badge className="bg-[#6B21D9]/15 text-[#A78BFA] border-[#6B21D9]/30 border text-[10px]">
                                {formatInfo.label} · {formatInfo.dimensions}
                            </Badge>
                        </div>
                        {channels.length === 0 ? (
                            <Card className="p-6 text-sm text-muted-foreground text-center bg-card border-border">
                                Selecione pelo menos um canal.
                            </Card>
                        ) : (
                            channels.map((ch) => (
                                <PreviewCard
                                    key={ch}
                                    channel={ch}
                                    caption={caption}
                                    hashtags={hashtags}
                                    mediaUrl={mediaUrl}
                                    mediaType={mediaType}
                                    formatInfo={formatInfo}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* Sticky footer */}
                <div className="sticky bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t border-border px-6 py-3 flex flex-wrap items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => save("rascunho")}
                        disabled={saving}
                        data-testid="post-editor-save-draft-button"
                    >
                        <Save className="h-4 w-4 mr-2" /> Guardar rascunho
                    </Button>
                    {scheduledFor && (
                        <Button
                            onClick={() => save("agendado")}
                            disabled={saving}
                            className="btn-grad-purple text-white"
                            data-testid="post-editor-schedule-button"
                        >
                            <CalendarClock className="h-4 w-4 mr-2" /> Agendar
                        </Button>
                    )}
                    <Button
                        onClick={() => save("publicado")}
                        disabled={saving}
                        className="bg-[#2DD4BF] text-[#0B0B10] hover:bg-[#26b8a6]"
                        data-testid="post-editor-publish-button"
                    >
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Publicar agora
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PreviewCard({ channel, caption, hashtags, mediaUrl, mediaType, formatInfo }) {
    const c = CHANNELS.find((x) => x.type === channel);
    if (!c) return null;
    const Icon = c.icon;
    return (
        <Card className="p-4 rounded-2xl bg-card border-border">
            <div className="flex items-center gap-2 mb-3">
                <span className={`h-8 w-8 rounded-lg ${c.bg} text-white grid place-items-center`}>
                    <Icon className="h-4 w-4" />
                </span>
                <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">@minhaempresa</div>
                </div>
            </div>
            {mediaUrl && (
                <div className={`w-full ${formatInfo.aspectClass} max-h-[420px] rounded-xl overflow-hidden bg-black mb-3`}>
                    {mediaType === "video" ? (
                        <video src={mediaUrl} controls className="w-full h-full object-cover" />
                    ) : (
                        <img src={mediaUrl} alt="" className="w-full h-full object-cover" />
                    )}
                </div>
            )}
            <p className="text-sm whitespace-pre-line">{caption || "— a sua legenda aparecerá aqui —"}</p>
            {hashtags.length > 0 && (
                <p className="text-xs text-[#A78BFA] mt-2">{hashtags.map((h) => `#${h}`).join(" ")}</p>
            )}
        </Card>
    );
}
